import { cloneElement, useCallback, useState } from 'react'
import {
  BuildingEditingTool,
  BuildingStructuralObjectTool,
} from '../../../../models/Construction'
import {
  Box,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@mui/material'
import { BeamsIcon, ListShowMoreIcon } from '../../../Svgs'
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedTool } from '../../../../actions/construction'
import { RootState } from '../../../../store'
import { getEnums } from '../../../../utils/Common'
import CreateStructuralElement from './CreateStructuralElement'
import EditStructuralElement from './EditStructuralElement'

const BuildingStructuralObjectToolsIcons: Record<
  BuildingStructuralObjectTool,
  JSX.Element
> = {
  [BuildingStructuralObjectTool.Beams]: <BeamsIcon />,
}

interface BuildingStructuralObjectToolsProps {
  isDisabled?: boolean
}

const StructuralObjectTools: React.FC<BuildingStructuralObjectToolsProps> = ({
  isDisabled,
}) => {
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(true)
  const selectedTool = useSelector(
    (state: RootState) => state.construction.selectedTool,
  )

  const renderSelectedTool = useCallback(() => {
    if (!selectedTool) {
      return null
    }

    switch (selectedTool) {
      case BuildingStructuralObjectTool.Beams:
        return <CreateStructuralElement />
      case BuildingEditingTool.DirectSelect:
        return <EditStructuralElement />
      default:
        return null
    }
  }, [selectedTool])

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          paddingLeft: '12px',
          paddingRight: '4px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          color='#04002E'
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            lineHeight: 'normal',
          }}
        >
          Add / Edit an Object
        </Typography>

        <IconButton
          onClick={() => setIsOpen((prev) => !prev)}
          sx={{
            transition: 'transform 0.3s',
            transform: isOpen ? 'rotate(90deg)' : 'rotate(180deg)',
          }}
        >
          <ListShowMoreIcon
            sx={{
              fontSize: '14px',
              color: '#797979',
            }}
          />
        </IconButton>
      </Box>

      <Divider
        flexItem
        orientation='horizontal'
        sx={{
          backgroundColor: '#E4E4E4',
        }}
      />

      <Collapse in={isOpen} timeout='auto' unmountOnExit>
        <Box
          sx={{
            flexGrow: 1,
            padding: '8px 12px',
          }}
        >
          <Grid container columns={10} columnSpacing='4px' rowSpacing='8px'>
            {getEnums(BuildingStructuralObjectTool).map(([key, value]) => {
              const isSelected = selectedTool === value
              return (
                <Grid key={key} size={2}>
                  <IconButton
                    disabled={isDisabled}
                    onClick={() => dispatch(setSelectedTool(value))}
                    sx={{
                      width: '100%',
                      borderRadius: '4px',
                      backgroundColor: isSelected ? '#1705E5' : '#EFEFEF',
                      '&:hover': {
                        backgroundColor: isSelected ? '#1505CE' : '#E2E2E2',
                      },
                    }}
                  >
                    {cloneElement(BuildingStructuralObjectToolsIcons[value], {
                      sx: {
                        color: isSelected ? 'white' : '#04002E',
                        fontSize: '24px',
                      },
                    })}
                  </IconButton>
                </Grid>
              )
            })}
          </Grid>
        </Box>

        <Divider
          flexItem
          orientation='horizontal'
          sx={{
            backgroundColor: '#E4E4E4',
          }}
        />
      </Collapse>

      {renderSelectedTool()}
    </Box>
  )
}

export default StructuralObjectTools
